import { useState } from 'react';
import apiClient from '../../api/apiClient';
import './WompiCheckoutButton.css';

interface WompiCheckoutButtonProps {
    planId: string; // "basic", "pro" or AddOnType
    salonId: string;
    salonName: string;
    userEmail: string;
    type?: 'PLAN' | 'ADDON';
    quantity?: number;
    buttonText?: string;
    className?: string;
    disabled?: boolean;
}

interface CheckoutSession {
    reference: string;
    publicKey: string;
    amountInCents: number;
    currency: string;
    signature: string;
    redirectUrl: string;
    customerEmail: string;
    customerName: string;
}

declare global {
    interface Window {
        WidgetCheckout?: new (config: WompiWidgetConfig) => WompiWidgetInstance;
    }
}

interface WompiWidgetConfig {
    currency: string;
    amountInCents: number;
    reference: string;
    publicKey: string;
    signature: { integrity: string };
    redirectUrl: string;
    customerData?: {
        email: string;
        fullName: string;
    };
}

interface WompiWidgetInstance {
    open: (callback: (result: WompiResult) => void) => void;
}

interface WompiResult {
    transaction?: {
        id: string;
        status: string;
    };
}

export default function WompiCheckoutButton({
    planId,
    salonId,
    salonName,
    userEmail,
    type,
    quantity,
    buttonText = 'Pagar Ahora',
    className = '',
    disabled = false,
}: WompiCheckoutButtonProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadWompiScript = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (window.WidgetCheckout) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://checkout.wompi.co/widget.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Wompi widget'));
            document.body.appendChild(script);
        });
    };

    const handlePayment = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // 1. Load Wompi script if not already loaded
            await loadWompiScript();

            // 2. Create checkout session in our backend
            const response = await apiClient.post<CheckoutSession>('/checkout/session', {
                planId,
                salonId,
                salonName,
                userEmail,
                type: type || 'PLAN',
                quantity: quantity || 1
            });

            const session = response.data;

            // 3. Open Wompi widget
            if (!window.WidgetCheckout) {
                throw new Error('Wompi widget not loaded');
            }

            const checkout = new window.WidgetCheckout({
                currency: session.currency,
                amountInCents: session.amountInCents,
                reference: session.reference,
                publicKey: session.publicKey,
                signature: {
                    integrity: session.signature,
                },
                redirectUrl: session.redirectUrl,
                customerData: {
                    email: session.customerEmail,
                    fullName: session.customerName,
                },
            });

            console.log('Opening Wompi widget for reference:', session.reference);
            checkout.open((result: WompiResult) => {
                console.log('Wompi widget result:', result);
                setIsLoading(false);

                if (result.transaction) {
                    console.log('Transaction completed:', result.transaction.id, 'Status:', result.transaction.status);
                } else {
                    console.log('Wompi widget closed without transaction');
                }
            });

            // Fallback: Si el widget se cierra y el callback no dispara, detectamos la desaparición del overlay
            setTimeout(() => {
                const checkClosed = setInterval(() => {
                    const overlay = document.querySelector('.wompi-widget-overlay');
                    if (!overlay) {
                        setIsLoading(false);
                        clearInterval(checkClosed);
                    }
                }, 2000);
                setTimeout(() => clearInterval(checkClosed), 600000);
            }, 3000); // Wait for widget to mount

        } catch (err) {
            console.error('Payment error:', err);
            setError('Error al iniciar el pago. Intenta de nuevo.');
            setIsLoading(false);
        }
    };

    return (
        <div className="wompi-checkout-container">
            <button
                className={`wompi-checkout-button ${className} ${isLoading ? 'loading' : ''}`}
                onClick={handlePayment}
                disabled={disabled || isLoading}
            >
                {isLoading ? (
                    <>
                        <span className="spinner"></span>
                        Cargando...
                    </>
                ) : (
                    buttonText
                )}
            </button>
            {error && <p className="wompi-error">{error}</p>}
        </div>
    );
}
